import { ReactNode, useEffect, useCallback } from 'react';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@/components/ui/IconButton';

interface ExpandableFormProps {
    /** Title displayed in the collapsed state. */
    title: string;
    /** Whether the form is currently expanded. */
    isExpanded: boolean;
    /** Callback when toggle button is clicked. */
    onToggle: () => void;
    /** Form content to display when expanded. */
    children: ReactNode;
    /** Action buttons to show in header when expanded (e.g., submit button). */
    headerActions?: ReactNode;
    /** Additional CSS classes for the container. */
    className?: string;
}

/**
 * A collapsible form container with animated expand/collapse behavior.
 * Shows a title with a toggle button in collapsed state.
 * Reveals full form content when expanded.
 * Supports Escape key to collapse when expanded.
 */
export function ExpandableForm({
    title,
    isExpanded,
    onToggle,
    children,
    headerActions,
    className = '',
}: ExpandableFormProps) {
    // Handle Escape key to collapse when expanded
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onToggle();
            }
        },
        [onToggle]
    );

    useEffect(() => {
        if (!isExpanded) return;

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isExpanded, handleKeyDown]);

    const containerClasses = isExpanded
        ? 'border border-accent dark:border-primary-light rounded-lg bg-active-light dark:bg-active-dark ring-1 ring-accent/20 dark:ring-primary-light/20'
        : 'border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-secondary';
    const roundedClass = isExpanded ? 'rounded-t-lg' : 'rounded-lg';
    const roundedAfterClass = isExpanded ? 'after:rounded-t-lg' : 'after:rounded-lg';

    return (
        <div className={`${containerClasses} ${className}`}>
            {/* Header - always visible; the title button covers the whole header */}
            <div
                className={`relative flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${roundedClass}`}
            >
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={isExpanded}
                    className={`min-w-0 flex-1 cursor-pointer text-left font-medium text-gray-700 dark:text-gray-200 focus:outline-hidden after:absolute after:inset-0 ${roundedAfterClass} focus-visible:after:ring-2 focus-visible:after:ring-accent dark:focus-visible:after:ring-primary-light`}
                >
                    {title}
                </button>
                <div className="relative flex items-center gap-2">
                    {isExpanded && headerActions}
                    <IconButton
                        variant="default"
                        size="md"
                        onClick={onToggle}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse form' : 'Expand form'}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        {isExpanded ? (
                            <MinusIcon className="h-5 w-5" />
                        ) : (
                            <PlusIcon className="h-5 w-5" />
                        )}
                    </IconButton>
                </div>
            </div>

            {/* Expandable content */}
            {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                    {children}
                </div>
            )}
        </div>
    );
}
